import React, { useState, useLayoutEffect } from 'react';
import SlideshowImage from 'modules/SlideshowImage';
import useFlags from '@/hooks/useFlags';
import useSlideShowContext from '../../stores/useSlideShowContext';

const { displaySlideGroupHeaders } = useFlags();

interface UseImageZoomProps {
  imageModels: { id?: string, image: { id: string }, zoomType: string }[];
  ref: React.MutableRefObject<HTMLDivElement>;
  skip: boolean;
}

const useImageZoom = ({ imageModels = [], ref, skip }: UseImageZoomProps) => {
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [questionImagesLoaded, setQuestionImagesLoaded] = useState(false);
  const readingLoaded = useSlideShowContext(state => state.readingLoaded);

  // AIDEV-NOTE: Prefer data-snippet to identify the imageModel for in-text images;
  // fall back to extracting the image ID from the src URL.
  const shouldUseZoomIcon = (img: HTMLImageElement) => {
    const snippet = img.getAttribute('data-snippet');
    const snippetIdMatch = snippet?.match(/\d+/);
    let imageModel;

    if (snippetIdMatch) {
      imageModel = imageModels.find(im => im.id?.toString() === snippetIdMatch[0]);
    }

    if (!imageModel) {
      const match = img.src.match(/\/(\d+)\//);
      const number = match ? match[1] : null;
      imageModel = imageModels.find(im => im.image.id.toString() === number?.toString());
    }

    return (!!imageModel?.zoomType || img.className === 'vocabImage' || img.classList.contains('slideReadingSectionImage')) && displaySlideGroupHeaders;
  };

  let timeout;

  useLayoutEffect(() => {
    if (skip) return undefined;
    if (!ref || !ref.current) return undefined;

    // Add an event listener to the iframe to set the loaded state.
    const iframe = ref.current.querySelector('iframe');
    if (iframe) {
      iframe.addEventListener('load', () => setIframeLoaded(true));
    }

    const readingSlide = ref.current.querySelector('div.slide-reading-section-text');

    // For drag and drop images, we need to wait for the questionLoaded event to ensure all images are loaded.
    const handleQuestionLoaded = () => { setQuestionImagesLoaded(true); };
    window.addEventListener('questionLoaded', handleQuestionLoaded);

    const vocabImages = ref.current.querySelectorAll('.vocabImage');

    if (imageModels.length === 0 && vocabImages.length === 0 && !iframe && !readingSlide) {
      return undefined;
    }

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      // Add zoom icon to all images in the iframe.
      if (iframe) {
        iframe.contentDocument.body.querySelectorAll('.fr-img-view, .trigger, .illustration').forEach((img) => {
          if (!slideshowImages.map(i => i.node).includes(img)) {
            const slideshowImage = new SlideshowImage();

            slideshowImage.init(img, true, true);
            setSlideshowImages(prev => [...prev, slideshowImage]);
          }
        });
      }

      // Images in reading slides loaded directly are sometimes nested in a div.
      // We'll need to add the zoom-url to teach image directly to properly handle zooming in the slide show.
      if (readingSlide) {
        readingSlide.querySelectorAll('img').forEach(((img: HTMLImageElement) => {
          // Add exception for small images and loc images from the vocabulary boxes.
          if (img.classList.contains('loc-img') || img.width <= 50 || img.height <= 50) return;

          if (!img.hasAttribute('data-zoomurl')) {
            img.setAttribute('data-zoomurl', img.src);
            img.classList.add('slideReadingSectionImage');
          }
        }));
      }

      // Add zoom icon to all images in the slide.
      ref.current.querySelectorAll('[data-zoomurl]').forEach((img) => {
        if (!slideshowImages.map(i => i.node).includes(img)) {
          if (img.getAttribute('init')) return;

          if (img.tagName === 'A') {
            const slideshowImage = new SlideshowImage();
            slideshowImage.init(img);
          } else {
            const slideshowImage = new SlideshowImage();
            slideshowImage.init(img, shouldUseZoomIcon(img as HTMLImageElement));
            setSlideshowImages(prev => [...prev, slideshowImage]);
          }
        }
      });
      // Give time for slide to finish loading all content before initializing js.
    }, 500);

    return () => {
      setIframeLoaded(false);
      setQuestionImagesLoaded(false);
      slideshowImages.forEach(slideshowImage => slideshowImage.closeModal());
      window.removeEventListener('questionLoaded', handleQuestionLoaded);
    };
  }, [ref && ref.current && ref.current.querySelectorAll('[data-zoomurl]'),
    displaySlideGroupHeaders, iframeLoaded, questionImagesLoaded, imageModels, readingLoaded, skip]);
};

export default useImageZoom;
